import React from 'react';
import { tokens } from '@ekelhaft-tools/eitelkeit/tokens';
import type { CanaryProgressProps } from './types.js';

/**
 * CanaryProgress - Visualizes canary rollout progress with traffic distribution
 *
 * Shows:
 * - Current vs target percentage
 * - Rollout status (ramping/paused/completed/rolling-back)
 * - Traffic distribution between canary and stable
 */
export function CanaryProgress({ current, target, status, traffic }: CanaryProgressProps) {
  const [isHovered, setIsHovered] = React.useState(false);

  // Status colors
  const statusColors = {
    ramping: tokens.colors.mint,
    paused: tokens.colors.mintDim,
    completed: tokens.colors.mint,
    'rolling-back': tokens.colors.void,
  };

  const statusLabels = {
    ramping: 'RAMPING UP',
    paused: 'PAUSED',
    completed: 'COMPLETED',
    'rolling-back': 'ROLLING BACK',
  };

  return (
    <div
      style={{
        backgroundColor: tokens.colors.void,
        border: `1px solid ${tokens.colors.border}`,
        borderRadius: tokens.radii.sm,
        padding: tokens.spacing.lg,
        fontFamily: tokens.fonts.mono,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: tokens.spacing.md,
        }}
      >
        <div
          style={{
            fontSize: tokens.fontSizes.sm,
            color: tokens.colors.mint,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Canary Rollout
        </div>
        <div
          style={{
            fontSize: tokens.fontSizes.xs,
            color: statusColors[status],
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontWeight: tokens.fontWeights.medium,
          }}
        >
          {statusLabels[status]}
        </div>
      </div>

      {/* Progress Bar Container */}
      <div
        style={{
          position: 'relative',
          height: '32px',
          backgroundColor: tokens.colors.border,
          borderRadius: tokens.radii.sm,
          marginBottom: tokens.spacing.md,
          overflow: 'hidden',
        }}
      >
        {/* Current Progress Fill */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100%',
            width: `${current}%`,
            backgroundColor: tokens.colors.mint,
            transition: 'width 0.3s ease-in-out',
            opacity: isHovered ? 0.9 : 1,
          }}
        />

        {/* Target Marker */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: `${target}%`,
            height: '100%',
            width: '2px',
            backgroundColor: tokens.colors.void,
            transform: 'translateX(-1px)',
          }}
        />

        {/* Percentage Labels */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: `0 ${tokens.spacing.sm}`,
            fontSize: tokens.fontSizes.xs,
            fontWeight: tokens.fontWeights.medium,
          }}
        >
          <span
            style={{
              color: current > 10 ? tokens.colors.void : tokens.colors.mint,
            }}
          >
            {current}%
          </span>
          <span
            style={{
              color: current > 90 ? tokens.colors.void : tokens.colors.mint,
            }}
          >
            TARGET: {target}%
          </span>
        </div>
      </div>

      {/* Traffic Distribution */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: tokens.spacing.md,
        }}
      >
        {/* Canary Traffic */}
        <div
          style={{
            padding: tokens.spacing.sm,
            backgroundColor: isHovered ? tokens.colors.border : 'transparent',
            border: `1px solid ${tokens.colors.border}`,
            borderRadius: tokens.radii.sm,
            transition: 'background-color 0.2s ease-in-out',
          }}
        >
          <div
            style={{
              fontSize: tokens.fontSizes.xs,
              color: tokens.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: tokens.spacing.xs,
            }}
          >
            Canary
          </div>
          <div
            style={{
              fontSize: tokens.fontSizes.lg,
              color: tokens.colors.mint,
              fontWeight: tokens.fontWeights.medium,
            }}
          >
            {traffic.canary}%
          </div>
        </div>

        {/* Stable Traffic */}
        <div
          style={{
            padding: tokens.spacing.sm,
            backgroundColor: isHovered ? tokens.colors.border : 'transparent',
            border: `1px solid ${tokens.colors.border}`,
            borderRadius: tokens.radii.sm,
            transition: 'background-color 0.2s ease-in-out',
          }}
        >
          <div
            style={{
              fontSize: tokens.fontSizes.xs,
              color: tokens.colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: tokens.spacing.xs,
            }}
          >
            Stable
          </div>
          <div
            style={{
              fontSize: tokens.fontSizes.lg,
              color: tokens.colors.mint,
              fontWeight: tokens.fontWeights.medium,
            }}
          >
            {traffic.stable}%
          </div>
        </div>
      </div>
    </div>
  );
}
